import { useCallback, useEffect, useMemo, useState } from "react";
import { getAuth } from "firebase/auth";
import { apiUrl, apiUrlDev } from "../../globalVariables";

const DASHBOARDS = [
  {
    id: "website-overview",
    label: "Cost Analytics",
    description:
      "Overall usage picture: clicks, auto-triggers, top users, and average actions per loan.",
    url: "https://us.posthog.com/project/300619/dashboard/1170714",
    embedUrl: "https://us.posthog.com/embedded/GbCqZMhzhZoOquDlYxprjwRTsUZw8g",
  },
  {
    id: "cost-and-accounts",
    label: "Product Analytics Overview",
    description:
      "Daily active users, account growth, and account counts by plan (enterprise, paid, free).",
    url: "https://us.posthog.com/shared/CzivfY-a4e-FgsEVoGpU0uqmMfuAHw",
    embedUrl: "https://us.posthog.com/shared/CzivfY-a4e-FgsEVoGpU0uqmMfuAHw",
  },
];

const formatDuration = (seconds) => {
  const totalSeconds = Number(seconds);
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return "-";
  const minutes = Math.floor(totalSeconds / 60);
  const remainder = Math.floor(totalSeconds % 60);
  return `${minutes}m ${String(remainder).padStart(2, "0")}s`;
};

const formatDateTime = (isoDate) => {
  if (!isoDate) return "-";
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

const DashboardPage = () => {
  const [selectedView, setSelectedView] = useState("dashboard");
  const [selectedDashboardId, setSelectedDashboardId] = useState(
    DASHBOARDS[0].id,
  );
  const [replays, setReplays] = useState([]);
  const [isReplayLoading, setIsReplayLoading] = useState(true);
  const [replayError, setReplayError] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [accountFilter, setAccountFilter] = useState("");
  const [dateRange, setDateRange] = useState("7d");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [activeReplayApiBase, setActiveReplayApiBase] = useState("");
  const [pagination, setPagination] = useState({
    count: null,
    page: 1,
    totalPages: null,
    limit: 25,
    hasNext: false,
    hasPrevious: false,
  });

  const selectedDashboard = useMemo(
    () =>
      DASHBOARDS.find((dashboard) => dashboard.id === selectedDashboardId) ??
      DASHBOARDS[0],
    [selectedDashboardId],
  );

  const fetchSessionReplays = useCallback(async () => {
    const userId = getAuth()?.currentUser?.uid;
    if (!userId) {
      setReplayError("Admin user not found. Please refresh and sign in again.");
      setIsReplayLoading(false);
      return;
    }

    setIsReplayLoading(true);
    setReplayError("");

    try {
      const isLocalhost =
        typeof window !== "undefined" &&
        (window.location.hostname === "localhost" ||
          window.location.hostname === "127.0.0.1");
      const baseUrls = activeReplayApiBase
        ? [activeReplayApiBase]
        : isLocalhost
          ? [apiUrlDev, apiUrl]
          : [apiUrl];
      let lastError = "Failed to load session replays";
      let loaded = false;

      for (const baseUrl of baseUrls) {
        try {
          const url = new URL(baseUrl + "/admin/session-replays");
          url.searchParams.append("userId", userId);
          url.searchParams.append("limit", String(pageSize));
          url.searchParams.append("page", String(page));
          if (emailFilter.trim()) {
            url.searchParams.append("emailQuery", emailFilter.trim());
          }
          if (accountFilter.trim()) {
            url.searchParams.append("accountQuery", accountFilter.trim());
          }
          if (dateRange === "7d") {
            url.searchParams.append("days", "7");
          } else if (dateRange === "30d") {
            url.searchParams.append("days", "30");
          } else if (dateRange === "custom") {
            if (fromDate) {
              url.searchParams.append("from", `${fromDate}T00:00:00.000Z`);
            }
            if (toDate) {
              url.searchParams.append("to", `${toDate}T23:59:59.999Z`);
            }
          }

          const controller = new AbortController();
          const requestTimeoutMs = baseUrl.includes("localhost") ? 90000 : 20000;
          const timeout = setTimeout(() => controller.abort(), requestTimeoutMs);
          const response = await fetch(url.toString(), { signal: controller.signal });
          clearTimeout(timeout);
          const responseText = await response.text();

          let payload = null;
          try {
            payload = JSON.parse(responseText);
          } catch (parseError) {
            const startsWithHtml = responseText.trim().startsWith("<!DOCTYPE");
            if (startsWithHtml) {
              lastError = `Replay API returned HTML from ${baseUrl}. If testing locally, make sure backend is running and CORS is correct.`;
            } else {
              lastError = `Replay API returned non-JSON from ${baseUrl}.`;
            }
            continue;
          }

          if (!response.ok || !payload?.success) {
            lastError = payload?.reason || `Replay API failed on ${baseUrl}`;
            continue;
          }

          setReplays(Array.isArray(payload.data) ? payload.data : []);
          setActiveReplayApiBase(baseUrl);
          setPagination({
            count:
              typeof payload?.pagination?.count === "number"
                ? payload.pagination.count
                : null,
            page: payload?.pagination?.page || page,
            totalPages:
              typeof payload?.pagination?.totalPages === "number"
                ? payload.pagination.totalPages
                : null,
            limit: payload?.pagination?.limit || pageSize,
            hasNext:
              Boolean(payload?.pagination?.hasNext) ||
              (Array.isArray(payload?.data) &&
                payload.data.length >= (payload?.pagination?.limit || pageSize)),
            hasPrevious: Boolean(payload?.pagination?.hasPrevious),
          });
          loaded = true;
          break;
        } catch (innerError) {
          if (innerError?.name === "AbortError") {
            lastError = `Replay API timed out at ${baseUrl}.`;
          } else {
            lastError = innerError?.message || `Could not reach ${baseUrl}`;
          }
        }
      }

      if (!loaded) {
        throw new Error(lastError);
      }
    } catch (error) {
      setReplayError(error?.message || "Failed to load session replays");
      setReplays([]);
      setPagination((prev) => ({
        ...prev,
        count: null,
        totalPages: null,
        hasNext: false,
        hasPrevious: false,
      }));
    } finally {
      setIsReplayLoading(false);
    }
  }, [
    dateRange,
    fromDate,
    toDate,
    page,
    pageSize,
    activeReplayApiBase,
    emailFilter,
    accountFilter,
  ]);

  useEffect(() => {
    if (selectedView === "replays") {
      fetchSessionReplays();
    }
  }, [selectedView, fetchSessionReplays]);

  const filteredReplays = useMemo(() => replays, [replays]);

  return (
    <div
      className="container-fluid DashboardPageContainer d-flex flex-column"
      style={{ height: "calc(100vh - 20px)", paddingTop: 12, paddingBottom: 12 }}
    >
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        {DASHBOARDS.map((dashboard) => {
          const isActive =
            selectedView === "dashboard" && selectedDashboard.id === dashboard.id;
          return (
            <button
              key={dashboard.id}
              type="button"
              className={`btn ${isActive ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => {
                setSelectedView("dashboard");
                setSelectedDashboardId(dashboard.id);
              }}
            >
              {dashboard.label}
            </button>
          );
        })}
        <button
          type="button"
          className={`btn ${selectedView === "replays" ? "btn-primary" : "btn-outline-primary"}`}
          onClick={() => setSelectedView("replays")}
        >
          Session Replays
        </button>
        <span className="ms-2 small text-muted">Switch view</span>
      </div>

      {selectedView === "dashboard" ? (
        <>
          <h2 className="mb-1">{selectedDashboard.label}</h2>
          <p className="mb-2 text-muted">{selectedDashboard.description}</p>

          <div
            className="mb-3 p-3 rounded-3"
            style={{
              background: "linear-gradient(135deg, #eef3ff 0%, #f6f0ff 100%)",
              border: "1px solid #c7d2fe",
            }}
          >
            <div className="d-flex align-items-start gap-3">
              <div
                className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                style={{ width: 40, height: 40, backgroundColor: "#4f46e5", color: "#fff", fontSize: "1.2rem" }}
              >
                &#8599;
              </div>
              <div>
                <div className="fw-semibold mb-1" style={{ fontSize: "1.1rem" }}>
                  Open in PostHog
                </div>
                <div className="text-muted mb-2" style={{ fontSize: "0.9rem" }}>
                  If the embedded view doesn&apos;t load or you need full access to filters, use the direct link below.
                </div>
                <a
                  href={selectedDashboard.url}
                  target="_blank"
                  rel="noreferrer"
                  className="fw-semibold"
                  style={{ fontSize: "1rem", color: "#4f46e5", textDecoration: "none", wordBreak: "break-all" }}
                >
                  {selectedDashboard.url} &rarr;
                </a>
              </div>
            </div>
          </div>

          <div
            className="w-100 flex-grow-1 border rounded bg-white overflow-hidden"
            style={{ height: "calc(100vh - 260px)", minHeight: "calc(100vh - 260px)" }}
          >
            <iframe
              title={selectedDashboard.label}
              frameBorder="0"
              allowFullScreen
              src={selectedDashboard.embedUrl}
              key={selectedDashboard.id}
              sandbox="allow-scripts allow-same-origin allow-popups"
              style={{ width: "100%", height: "100%", border: 0, display: "block" }}
            />
          </div>
        </>
      ) : null}

      {selectedView === "replays" ? (
      <div className="mt-2 p-3 border rounded bg-white">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <h3 className="mb-0">Session Replays</h3>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={fetchSessionReplays}
          >
            Refresh
          </button>
        </div>

        <div
          className="mb-3 p-3 rounded-3"
          style={{
            background: "linear-gradient(135deg, #eef3ff 0%, #f6f0ff 100%)",
            border: "1px solid #c7d2fe",
          }}
        >
          <div className="d-flex align-items-start gap-3">
            <div
              className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
              style={{ width: 40, height: 40, backgroundColor: "#4f46e5", color: "#fff", fontSize: "1.2rem" }}
            >
              &#9654;
            </div>
            <div>
              <div className="fw-semibold mb-1" style={{ fontSize: "1.1rem" }}>
                Browse all replays in PostHog
              </div>
              <div className="text-muted mb-2" style={{ fontSize: "0.9rem" }}>
                For full playback controls, advanced filters, and event timelines, open session replays directly in PostHog.
              </div>
              <a
                href="https://us.posthog.com/project/300619/replay/home"
                target="_blank"
                rel="noreferrer"
                className="fw-semibold"
                style={{ fontSize: "1rem", color: "#4f46e5", textDecoration: "none", wordBreak: "break-all" }}
              >
                https://us.posthog.com/project/300619/replay/home &rarr;
              </a>
            </div>
          </div>
        </div>

        <div className="d-flex flex-wrap gap-2 mb-3">
          <input
            className="form-control"
            style={{ maxWidth: 260 }}
            placeholder="Filter by user email"
            value={emailFilter}
            onChange={(e) => {
              setEmailFilter(e.target.value);
              setPage(1);
            }}
          />
          <input
            className="form-control"
            style={{ maxWidth: 240 }}
            placeholder="Filter by account"
            value={accountFilter}
            onChange={(e) => {
              setAccountFilter(e.target.value);
              setPage(1);
            }}
          />
          <button
            type="button"
            className={`btn ${dateRange === "7d" ? "btn-primary" : "btn-outline-secondary"}`}
            onClick={() => {
              setDateRange("7d");
              setPage(1);
            }}
          >
            Last 7 days
          </button>
          <button
            type="button"
            className={`btn ${dateRange === "30d" ? "btn-primary" : "btn-outline-secondary"}`}
            onClick={() => {
              setDateRange("30d");
              setPage(1);
            }}
          >
            Last 30 days
          </button>
          <button
            type="button"
            className={`btn ${dateRange === "all" ? "btn-primary" : "btn-outline-secondary"}`}
            onClick={() => {
              setDateRange("all");
              setPage(1);
            }}
          >
            All
          </button>
          <button
            type="button"
            className={`btn ${dateRange === "custom" ? "btn-primary" : "btn-outline-secondary"}`}
            onClick={() => {
              setDateRange("custom");
              setPage(1);
            }}
          >
            Custom
          </button>
          {dateRange === "custom" ? (
            <>
              <input
                type="date"
                className="form-control"
                style={{ maxWidth: 170 }}
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setPage(1);
                }}
              />
              <input
                type="date"
                className="form-control"
                style={{ maxWidth: 170 }}
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setPage(1);
                }}
              />
            </>
          ) : null}
        </div>

        {replayError ? (
          <div className="alert alert-warning py-2 mb-3">
            {replayError}
          </div>
        ) : null}

        <div className="table-responsive">
          <table className="table table-sm align-middle">
            <thead>
              <tr>
                <th>User</th>
                <th>Account</th>
                <th>Started</th>
                <th>Duration</th>
                <th>Session</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {isReplayLoading ? (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-4">
                    Loading session replays...
                  </td>
                </tr>
              ) : filteredReplays.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-4">
                    No session replays found.
                  </td>
                </tr>
              ) : (
                filteredReplays.map((replay) => {
                  const accountName = replay?.accountName || "-";
                  const displayUser = replay?.userEmail
                    ? replay.userEmail
                    : replay?.distinctId
                      ? `ID: ${replay.distinctId}`
                      : "Unknown";
                  return (
                    <tr key={replay.id || replay.replayUrl}>
                      <td title={replay?.userName || ""}>{displayUser}</td>
                      <td>{accountName}</td>
                      <td>{formatDateTime(replay.startedAt)}</td>
                      <td>{formatDuration(replay.durationSec)}</td>
                      <td>{replay.id || "-"}</td>
                      <td>
                        <a
                          className="btn btn-sm btn-primary"
                          href={replay.replayUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Open
                        </a>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="d-flex flex-wrap justify-content-between align-items-center mt-2 gap-2">
          <div className="small text-muted">
            Showing page {pagination.page}
            {pagination.totalPages ? ` of ${pagination.totalPages}` : ""}
            {typeof pagination.count === "number"
              ? ` (${pagination.count} total)`
              : ""}
          </div>
          <div className="d-flex align-items-center gap-2">
            <select
              className="form-select form-select-sm"
              style={{ width: 90 }}
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={isReplayLoading || (!pagination.hasPrevious && page <= 1)}
            >
              Previous
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() =>
                setPage((p) =>
                  pagination.totalPages
                    ? Math.min(pagination.totalPages, p + 1)
                    : p + 1
                )
              }
              disabled={isReplayLoading || !pagination.hasNext}
            >
              Next
            </button>
          </div>
        </div>
      </div>
      ) : null}
    </div>
  );
};

export default DashboardPage;
